import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  Loader2, 
  AlertCircle,
  CheckCircle2,
  ArrowRight
} from 'lucide-react'
import { marketsApi, polymarketApi, tradesApi } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import { cn } from '../lib/utils'
import { useWallet } from '../hooks/useWallet'

interface TradeModalProps {
  isOpen: boolean
  onClose: () => void
  marketId: string
  marketName: string
  currentPrice?: number
  yesTokenId?: string
  noTokenId?: string
}

export function TradeModal({ 
  isOpen, 
  onClose, 
  marketId, 
  marketName,
  currentPrice = 0.5,
  yesTokenId,
  noTokenId 
}: TradeModalProps) {
  const [step, setStep] = useState<'input' | 'review' | 'sign' | 'submitting' | 'success' | 'error'>('input')
  const [side, setSide] = useState<'YES' | 'NO'>('YES')
  const [amount, setAmount] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [showPrivateKey, setShowPrivateKey] = useState(false)
  const [signedOrder, setSignedOrder] = useState<any>(null)
  const [error, setError] = useState('')

  const { isConnected, address, balance, connect } = useWallet()

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('input')
      setSide('YES')
      setAmount('')
      setMaxPrice(currentPrice.toFixed(3))
      setPrivateKey('')
      setSignedOrder(null)
      setError('')
    }
  }, [isOpen, currentPrice])

  // Fetch market details
  const { data: marketData } = useQuery({
    queryKey: ['market', marketId],
    queryFn: () => marketsApi.getById(marketId).then(r => r.data),
    enabled: isOpen && !!marketId,
  })

  // Generate order data mutation
  const generateOrderMutation = useMutation({
    mutationFn: () => polymarketApi.generateOrderData({
      marketId,
      side,
      size: parseFloat(amount),
      maxPrice: parseFloat(maxPrice),
    }),
    onSuccess: (response) => {
      setSignedOrder(response.data)
      setStep('sign')
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to generate order')
      setStep('error')
    },
  })

  // Submit signed order mutation
  const submitOrderMutation = useMutation({
    mutationFn: () => polymarketApi.submitSignedOrder({
      signedOrder,
      marketId,
      side,
      size: parseFloat(amount),
      price: parseFloat(maxPrice),
    }),
    onSuccess: () => {
      setStep('success')
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to submit order')
      setStep('error')
    },
  })

  // Record trade in our system
  const recordTradeMutation = useMutation({
    mutationFn: () => tradesApi.execute({
      marketId,
      side,
      amount: parseFloat(amount),
      maxPrice: parseFloat(maxPrice),
    }),
  })

  const handleReview = () => {
    if (!amount || parseFloat(amount) <= 0) return
    if (!maxPrice || parseFloat(maxPrice) <= 0 || parseFloat(maxPrice) > 1) return
    setStep('review')
  }

  const handleSign = async () => {
    if (!privateKey) return
    
    try {
      // Import ethers dynamically
      const { Wallet } = await import('ethers')
      const wallet = new Wallet(privateKey)
      
      // Generate order data first
      const orderData = await polymarketApi.generateOrderData({
        marketId,
        side,
        size: parseFloat(amount),
        maxPrice: parseFloat(maxPrice),
      })

      // Sign the order data
      const signature = await wallet.signMessage(JSON.stringify(orderData.data))
      
      setSignedOrder({
        ...orderData.data,
        signature,
        signer: wallet.address,
      })
      
      setStep('submitting')
      
      // Submit the signed order
      await polymarketApi.submitSignedOrder({
        signedOrder: {
          ...orderData.data,
          signature,
          signer: wallet.address,
        },
        marketId,
        side,
        size: parseFloat(amount),
        price: parseFloat(maxPrice),
      })

      // Record in our system
      await recordTradeMutation.mutateAsync()
      
      setStep('success')
    } catch (err: any) {
      setError(err.message || 'Failed to sign order')
      setStep('error')
    }
  }

  const handleClose = () => {
    onClose()
    // Reset after animation
    setTimeout(() => {
      setStep('input')
      setError('')
    }, 300)
  }

  const calculatePotentialReturn = () => {
    const amt = parseFloat(amount) || 0
    const price = parseFloat(maxPrice) || 0
    if (side === 'YES') {
      return amt * (1 - price) // Profit if YES resolves
    } else {
      return amt * (1 - price) // Profit if NO resolves
    }
  }

  const calculateTotalCost = () => {
    const amt = parseFloat(amount) || 0
    const price = parseFloat(maxPrice) || 0
    return amt * price
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {step === 'success' ? 'Order Placed!' : 'Place Order'}
            </h2>
            <p className="text-sm text-gray-500 truncate max-w-xs">{marketName}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Input */}
        {step === 'input' && (
          <div className="p-6 space-y-6">
            {/* Wallet Connection */}
            {!isConnected ? (
              <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Wallet className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="font-medium text-blue-900">Connect Wallet</p>
                    <p className="text-sm text-blue-700">Required for trading</p>
                  </div>
                </div>
                <button
                  onClick={connect}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Connect
                </button>
              </div>
            ) : (
              <div className="bg-green-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Wallet className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <p className="font-medium text-green-900">Wallet Connected</p>
                    <p className="text-sm text-green-700 truncate max-w-[200px]">{address}</p>
                  </div>
                </div>
                <span className="text-sm font-medium text-green-800">
                  {parseFloat(balance).toFixed(4)} USDC
                </span>
              </div>
            )}

            {/* Side Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSide('YES')}
                  className={cn(
                    'flex items-center justify-center px-4 py-3 rounded-lg border-2 font-medium transition-colors',
                    side === 'YES'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Buy YES
                </button>
                <button
                  onClick={() => setSide('NO')}
                  className={cn(
                    'flex items-center justify-center px-4 py-3 rounded-lg border-2 font-medium transition-colors',
                    side === 'NO'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  Buy NO
                </button>
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (Shares)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                  shares
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Max position: {formatCurrency(10000)}
              </p>
            </div>

            {/* Price Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Price per Share
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="0.500"
                  min="0.01"
                  max="0.99"
                  step="0.001"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                  USDC
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Market price: {currentPrice.toFixed(3)} USDC
              </p>
            </div>

            {/* Summary */}
            {amount && maxPrice && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Cost</span>
                  <span className="font-medium">{formatCurrency(calculateTotalCost())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Potential Return</span>
                  <span className="font-medium text-green-600">
                    +{formatCurrency(calculatePotentialReturn())}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Return %</span>
                  <span className="font-medium text-green-600">
                    {((calculatePotentialReturn() / calculateTotalCost()) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}

            {/* Review Button */}
            <button
              onClick={handleReview}
              disabled={!isConnected || !amount || parseFloat(amount) <= 0 || !maxPrice}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Review Order
            </button>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 'review' && (
          <div className="p-6 space-y-6">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Market</span>
                <span className="font-medium text-right max-w-[200px] truncate">{marketName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Position</span>
                <span className={cn(
                  'font-medium',
                  side === 'YES' ? 'text-green-600' : 'text-red-600'
                )}>
                  Buy {side}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount</span>
                <span className="font-medium">{parseFloat(amount).toFixed(2)} shares</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Max Price</span>
                <span className="font-medium">{parseFloat(maxPrice).toFixed(3)} USDC</span>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Cost</span>
                  <span className="font-bold">{formatCurrency(calculateTotalCost())}</span>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">
                You will need to provide your private key to sign this transaction. 
                Your key is used locally in your browser and never sent to our servers.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('input')}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setStep('sign')}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Sign */}
        {step === 'sign' && (
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Sign Transaction</h3>
              <p className="text-gray-500 mt-1">
                Enter your private key to sign this order
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Private Key
              </label>
              <div className="relative">
                <input
                  type={showPrivateKey ? 'text' : 'password'}
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  placeholder="0x..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPrivateKey(!showPrivateKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPrivateKey ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="mt-2 text-sm text-red-600">
                ⚠️ Never share your private key. It is only used locally in your browser.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('review')}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleSign}
                disabled={!privateKey || privateKey.length < 64}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Sign & Submit
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Submitting */}
        {step === 'submitting' && (
          <div className="p-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Submitting Order</h3>
            <p className="text-gray-500 mt-1">Please wait while we process your transaction...</p>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 'success' && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Order Submitted!</h3>
            <p className="text-gray-500 mt-1">
              Your {side} position for {parseFloat(amount).toFixed(2)} shares has been placed.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Order Failed</h3>
              <p className="text-red-600 mt-2">{error}</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('input')}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                Start Over
              </button>
              <button
                onClick={handleClose}
                className="flex-1 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
